/**
 * StoryboardsAndVideosDialog — Feature 4.
 * Wide modal with left sidebar (every lesson + status pills) + right pane
 * editor (Video title + Slide N cards). Keyboard ← / → nav. Bulk actions:
 * "Generate all storyboards" (concurrency 3) + "Render all videos"
 * (concurrency 3). Each render registers with the global AI Jobs tray.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Film, Plus, ChevronLeft, ChevronRight, Sparkles, Save, RotateCcw,
    PlayCircle, Loader2, CheckCircle2, AlertTriangle, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { useAiJobs } from "@/lib/ai-jobs";


function storyboardMinutes(storyboard) {
    if (!storyboard) return 0;
    const slides = storyboard.slides || [];
    const secs = storyboard.estimated_duration_sec || slides.length * 35;
    return Math.max(1, Math.round(secs / 60));
}

async function runWithConcurrency(items, concurrency, worker) {
    const queue = items.slice();
    const inFlight = new Set();
    const failures = [];
    async function next() {
        const item = queue.shift();
        if (!item) return;
        const p = (async () => {
            try { await worker(item); }
            catch (e) { failures.push({ item, error: e }); }
        })();
        inFlight.add(p);
        p.finally(() => inFlight.delete(p));
        if (queue.length && inFlight.size >= concurrency) {
            await Promise.race(inFlight);
        }
        if (queue.length) return next();
        await Promise.all(inFlight);
    }
    await next();
    return failures;
}


export default function StoryboardsAndVideosDialog({
    open, onOpenChange, chapter,
}) {
    const { trackJob, jobs } = useAiJobs();
    const [loading, setLoading] = useState(true);
    const [lessons, setLessons] = useState([]);
    const [activeIdx, setActiveIdx] = useState(0);
    const [editing, setEditing] = useState(null); // editable copy of current storyboard
    const [dirty, setDirty] = useState(false);
    const [generatingId, setGeneratingId] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [genAllBusy, setGenAllBusy] = useState(false);
    const [renderAllBusy, setRenderAllBusy] = useState(false);

    const load = useCallback(async () => {
        if (!chapter?.id) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/chapters/${chapter.id}/lessons`);
            setLessons(data || []);
        } catch (e) {
            toast.error("Could not load lessons.");
        } finally {
            setLoading(false);
        }
    }, [chapter?.id]);

    useEffect(() => { if (open) { load(); setActiveIdx(0); } }, [open, load]);

    // Refresh after any tray-event for this chapter (new video, etc.)
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (!e.detail) return;
            if (e.detail.lessonId) load();
            if (e.detail.chapterId === chapter?.id) load();
        };
        window.addEventListener("cco:lesson-video-rendered", handler);
        window.addEventListener("cco:chapter-lessons-changed", handler);
        return () => {
            window.removeEventListener("cco:lesson-video-rendered", handler);
            window.removeEventListener("cco:chapter-lessons-changed", handler);
        };
    }, [open, chapter?.id, load]);

    const activeLesson = lessons[activeIdx] || null;

    // Hydrate the editing copy when lesson changes
    useEffect(() => {
        if (!activeLesson) { setEditing(null); setDirty(false); return; }
        const sb = activeLesson.video_storyboard;
        setEditing(sb
            ? JSON.parse(JSON.stringify(sb))
            : { video_title: "", slides: [], estimated_duration_sec: 0 },
        );
        setDirty(false);
    }, [activeLesson?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keyboard nav
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
            if (e.key === "ArrowLeft" && activeIdx > 0) setActiveIdx(activeIdx - 1);
            if (e.key === "ArrowRight" && activeIdx < lessons.length - 1) setActiveIdx(activeIdx + 1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, activeIdx, lessons.length]);

    const lessonStatusMap = useMemo(() => {
        const map = {};
        for (const j of jobs) {
            if (j.kind !== "video" || !j.lessonId) continue;
            if (!j.done && !j.error) map[j.lessonId] = { kind: "rendering", percent: j.percent };
            else if (j.error) map[j.lessonId] = { kind: "error", error: j.error };
        }
        if (generatingId) map[generatingId] = { kind: "generating" };
        return map;
    }, [jobs, generatingId]);

    const missingStoryboardCount = useMemo(
        () => lessons.filter((l) => !l.video_storyboard).length,
        [lessons],
    );

    const generateOne = async (lessonId) => {
        setGeneratingId(lessonId);
        try {
            const { data } = await api.post(`/lessons/${lessonId}/storyboard/generate`);
            setLessons((prev) => prev.map(
                (l) => l.id === lessonId ? { ...l, video_storyboard: data.storyboard } : l,
            ));
            return data.storyboard;
        } finally {
            setGeneratingId(null);
        }
    };

    const handleGenerate = async () => {
        if (!activeLesson) return;
        try {
            await generateOne(activeLesson.id);
            toast.success("Storyboard generated.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed.");
        }
    };

    const handleSave = async () => {
        if (!activeLesson || !editing) return;
        setSavingId(activeLesson.id);
        try {
            await api.put(`/lessons/${activeLesson.id}`, {
                video_storyboard: editing,
            });
            setLessons((prev) => prev.map(
                (l) => l.id === activeLesson.id ? { ...l, video_storyboard: editing } : l,
            ));
            setDirty(false);
            toast.success("Edits saved.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSavingId(null);
        }
    };

    const handleRender = async (lesson) => {
        if (!lesson?.video_storyboard?.slides?.length) {
            toast.error("Lesson has no storyboard yet."); return;
        }
        // Auto-save dirty edits on the active lesson before kicking the render
        if (lesson.id === activeLesson?.id && dirty) {
            await handleSave();
        }
        try {
            const { data } = await api.post(`/lessons/${lesson.id}/render-video`, {});
            trackJob({
                kind: "video",
                jobId: data.job_id,
                lessonId: lesson.id,
                lessonTitle: lesson.title,
                chapterId: chapter?.id,
                chapterTitle: chapter?.title,
                total: data.total || (lesson.video_storyboard.slides?.length || 1),
            });
            toast.success(`Render queued for "${lesson.title}".`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Render failed to kick.");
        }
    };

    const generateAllMissing = async () => {
        const missing = lessons.filter((l) => !l.video_storyboard);
        if (missing.length === 0) return;
        if (!window.confirm(`Generate ${missing.length} storyboard(s)? Runs 3 at a time.`)) return;
        setGenAllBusy(true);
        const failures = await runWithConcurrency(missing, 3, async (lesson) => {
            await generateOne(lesson.id);
        });
        setGenAllBusy(false);
        if (failures.length) {
            toast.error(`${failures.length} failed. The rest are saved.`);
        } else {
            toast.success(`Generated ${missing.length} storyboards.`);
        }
    };

    const renderAll = async () => {
        const renderable = lessons.filter((l) => l.video_storyboard?.slides?.length);
        if (renderable.length === 0) {
            toast.error("No lessons have a storyboard yet."); return;
        }
        if (!window.confirm(`Queue ${renderable.length} video render(s)? Track progress in the AI jobs tray.`)) return;
        setRenderAllBusy(true);
        // Auto-save dirty edits before fanning out
        if (dirty) await handleSave();
        await runWithConcurrency(renderable, 3, async (lesson) => {
            const { data } = await api.post(`/lessons/${lesson.id}/render-video`, {});
            trackJob({
                kind: "video",
                jobId: data.job_id,
                lessonId: lesson.id,
                lessonTitle: lesson.title,
                chapterId: chapter?.id,
                chapterTitle: chapter?.title,
                total: data.total || (lesson.video_storyboard.slides?.length || 1),
            });
        });
        setRenderAllBusy(false);
        toast.success(`Queued ${renderable.length} renders.`);
    };

    // Editable fields
    const setSlide = (idx, patch) => {
        setEditing((e) => ({
            ...e,
            slides: e.slides.map((s, i) => i === idx ? { ...s, ...patch } : s),
        }));
        setDirty(true);
    };
    const addSlide = () => {
        setEditing((e) => ({
            ...e,
            slides: [...(e?.slides || []), { heading: "", bullets: [], narration: "" }],
        }));
        setDirty(true);
    };
    const removeSlide = (idx) => {
        setEditing((e) => ({ ...e, slides: e.slides.filter((_, i) => i !== idx) }));
        setDirty(true);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
            <DialogContent
                className="max-w-6xl w-[96vw] h-[88vh] p-0 overflow-hidden flex flex-col"
                data-testid="storyboards-videos-dialog"
            >
                <DialogHeader className="px-5 pt-4 pb-3 border-b">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <DialogTitle className="font-display text-xl flex items-center gap-2">
                                <Film className="h-5 w-5 text-primary" />
                                Storyboards & videos — {chapter?.title || "Chapter"}
                            </DialogTitle>
                            <DialogDescription className="text-xs mt-0.5">
                                Author the per-lesson video storyboard, then render. Edits
                                auto-save before rendering. Use ← / → to navigate lessons.
                            </DialogDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={generateAllMissing}
                                disabled={genAllBusy || missingStoryboardCount === 0}
                                className="gap-1.5"
                                data-testid="generate-all-storyboards-btn"
                            >
                                {genAllBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                                Generate all storyboards
                                {missingStoryboardCount > 0 ? (
                                    <Badge variant="outline" className="ml-1 text-[10px]">
                                        {missingStoryboardCount} missing
                                    </Badge>
                                ) : null}
                            </Button>
                            <Button
                                size="sm"
                                onClick={renderAll}
                                disabled={renderAllBusy || lessons.every((l) => !l.video_storyboard)}
                                className="gap-1.5 bg-primary hover:bg-primary/90"
                                data-testid="render-all-videos-btn"
                            >
                                {renderAllBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                                Render all videos
                            </Button>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 flex min-h-0">
                    {/* Sidebar — every lesson */}
                    <aside
                        className="w-[260px] border-r border-border bg-muted/20 overflow-y-auto"
                        data-testid="storyboard-lesson-sidebar"
                    >
                        {loading ? (
                            <div className="p-3 space-y-2">
                                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
                            </div>
                        ) : lessons.length === 0 ? (
                            <div className="p-6 text-xs text-muted-foreground text-center">
                                This chapter has no lessons yet.
                            </div>
                        ) : (
                            <ul className="py-1">
                                {lessons.map((l, i) => {
                                    const sb = l.video_storyboard;
                                    const mins = storyboardMinutes(sb);
                                    const slides = sb?.slides?.length || 0;
                                    const live = lessonStatusMap[l.id];
                                    return (
                                        <li key={l.id}>
                                            <button
                                                type="button"
                                                onClick={() => setActiveIdx(i)}
                                                className={`w-full text-left px-3 py-2 border-l-2 ${
                                                    i === activeIdx
                                                        ? "bg-primary/10 border-primary"
                                                        : "border-transparent hover:bg-muted/40"
                                                }`}
                                                data-testid={`storyboard-lesson-${l.id}`}
                                            >
                                                <div className="text-xs font-medium truncate">
                                                    {i + 1}. {l.title || "Untitled"}
                                                </div>
                                                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                                    {sb ? (
                                                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-1.5 py-0">
                                                            ~{mins} min · {slides} slides
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                            No storyboard
                                                        </Badge>
                                                    )}
                                                    {live?.kind === "rendering" ? (
                                                        <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0 gap-0.5">
                                                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                                            Rendering {live.percent}%
                                                        </Badge>
                                                    ) : null}
                                                    {live?.kind === "generating" ? (
                                                        <Badge className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0 gap-0.5">
                                                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                                            Generating
                                                        </Badge>
                                                    ) : null}
                                                    {live?.kind === "error" ? (
                                                        <Badge className="bg-rose-100 text-rose-700 text-[10px] px-1.5 py-0 gap-0.5">
                                                            <AlertTriangle className="h-2.5 w-2.5" />
                                                            Error
                                                        </Badge>
                                                    ) : null}
                                                </div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </aside>

                    {/* Editor */}
                    <section className="flex-1 overflow-y-auto p-5" data-testid="storyboard-editor">
                        {activeLesson ? (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setActiveIdx(Math.max(0, activeIdx - 1))}
                                            disabled={activeIdx === 0}
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <span className="text-xs text-muted-foreground font-mono">
                                            {activeIdx + 1} / {lessons.length}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setActiveIdx(Math.min(lessons.length - 1, activeIdx + 1))}
                                            disabled={activeIdx >= lessons.length - 1}
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleGenerate}
                                            disabled={generatingId === activeLesson.id}
                                            className="gap-1.5"
                                            data-testid="generate-storyboard-btn"
                                        >
                                            {generatingId === activeLesson.id
                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                : activeLesson.video_storyboard ? <RotateCcw className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
                                            {activeLesson.video_storyboard ? "Regenerate" : "Generate storyboard"}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleSave}
                                            disabled={!dirty || savingId === activeLesson.id}
                                            className="gap-1.5"
                                            data-testid="save-storyboard-btn"
                                        >
                                            {savingId === activeLesson.id
                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                : <Save className="h-3.5 w-3.5" />}
                                            {dirty ? "Save edits" : "Saved"}
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => handleRender(activeLesson)}
                                            disabled={!editing?.slides?.length}
                                            className="gap-1.5 bg-primary hover:bg-primary/90"
                                            data-testid="render-storyboard-btn"
                                        >
                                            <PlayCircle className="h-3.5 w-3.5" />
                                            Render this video
                                        </Button>
                                    </div>
                                </div>

                                <h2 className="font-display text-lg text-foreground mb-1">
                                    {activeLesson.title}
                                </h2>

                                {editing ? (
                                    <div className="space-y-4">
                                        <div>
                                            <Label className="text-xs font-semibold">Video title</Label>
                                            <Input
                                                value={editing.video_title || ""}
                                                onChange={(e) => { setEditing({ ...editing, video_title: e.target.value }); setDirty(true); }}
                                                placeholder="Spoken video title…"
                                                data-testid="video-title-input"
                                            />
                                        </div>

                                        {(editing.slides || []).map((slide, idx) => (
                                            <div
                                                key={idx}
                                                className="rounded-md border border-border p-3 bg-card"
                                                data-testid={`slide-${idx}`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-primary">
                                                        Slide {idx + 1}
                                                    </div>
                                                    <Button
                                                        size="sm" variant="ghost"
                                                        className="text-rose-600 hover:bg-rose-50 h-7 text-xs"
                                                        onClick={() => removeSlide(idx)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                                <div className="space-y-2">
                                                    <div>
                                                        <Label className="text-[11px]">Heading</Label>
                                                        <Input
                                                            value={slide.heading || ""}
                                                            onChange={(e) => setSlide(idx, { heading: e.target.value })}
                                                            placeholder="Slide heading…"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-[11px]">Bullets (one per line)</Label>
                                                        <Textarea
                                                            value={Array.isArray(slide.bullets) ? slide.bullets.join("\n") : (slide.bullets || "")}
                                                            onChange={(e) => setSlide(idx, {
                                                                bullets: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                                                            })}
                                                            placeholder="Up to 4 bullets, one per line…"
                                                            rows={3}
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-[11px]">Narration</Label>
                                                        <Textarea
                                                            value={slide.narration || ""}
                                                            onChange={(e) => setSlide(idx, { narration: e.target.value })}
                                                            placeholder="What the TTS voice will say for this slide…"
                                                            rows={3}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-1.5"
                                            onClick={addSlide}
                                            data-testid="add-slide-btn"
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                            Add slide
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-muted-foreground text-sm">
                                        <Film className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        Click "Generate storyboard" to get a draft from the lesson content.
                                    </div>
                                )}
                            </>
                        ) : loading ? (
                            <Skeleton className="h-40 w-full" />
                        ) : (
                            <div className="text-center py-16 text-muted-foreground">
                                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                Pick a lesson from the sidebar to begin.
                            </div>
                        )}
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
}
